import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  formatPeriodDate,
  getPeriodPresets,
  startOfDay,
  validatePeriod,
} from "./dashboardStatisticsUtils";
import "./dashboardStatistics.scss";

const DATEPICKER_PROPS = {
  dateFormat: "MMM d, yyyy",
  calendarClassName: "dashboard-datepicker-calendar",
  popperClassName: "dashboard-datepicker-popper",
  popperPlacement: "bottom-start",
  showPopperArrow: true,
  isClearable: false,
  autoComplete: "off",
  popperProps: {
    strategy: "fixed",
  },
  popperModifiers: [
    {
      name: "preventOverflow",
      options: {
        rootBoundary: "viewport",
        altBoundary: true,
        tether: false,
      },
    },
    {
      name: "flip",
      options: {
        fallbackPlacements: ["bottom-start", "top-start", "bottom-end", "top-end"],
      },
    },
  ],
};

const DashboardPeriodPicker = ({
  periodFrom,
  periodTo,
  onPeriodFromChange,
  onPeriodToChange,
  onApply,
  resolvedPeriod,
  activePresetId,
  onPresetSelect,
}) => {
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState({});
  const presets = getPeriodPresets();

  useEffect(() => {
    if (Object.keys(validationErrors).length === 0) return;
    const { errors } = validatePeriod(periodFrom, periodTo);
    if (Object.keys(errors).length === 0) setValidationErrors({});
  }, [periodFrom, periodTo, validationErrors]);

  const resolvedLabel =
    resolvedPeriod?.periodFrom && resolvedPeriod?.periodTo
      ? `${formatPeriodDate(new Date(resolvedPeriod.periodFrom))} – ${formatPeriodDate(new Date(resolvedPeriod.periodTo))}`
      : null;

  const errorMessage = (key) => {
    if (key === "required") return t("app.analytics.periodRequired");
    if (key === "endBeforeStart") return t("app.analytics.periodEndBeforeStart");
    if (key === "maxRange") return t("app.analytics.periodMaxRange");
    return null;
  };

  const handleApply = () => {
    const { valid, errors } = validatePeriod(periodFrom, periodTo);
    if (!valid) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    onApply();
  };

  const handlePreset = (preset) => {
    const range = preset.getRange();
    setValidationErrors({});
    if (onPresetSelect) {
      onPresetSelect(preset.id, range.periodFrom, range.periodTo);
    } else {
      onPeriodFromChange(range.periodFrom);
      onPeriodToChange(range.periodTo);
    }
  };

  const presetLabel = (id) => {
    const keys = {
      thisMonth: "app.analytics.presetThisMonth",
      lastMonth: "app.analytics.presetLastMonth",
      last3Months: "app.analytics.presetLast3Months",
      thisYear: "app.analytics.presetThisYear",
    };
    return t(keys[id] || id);
  };

  return (
    <div className="dashboard-period-toolbar shadow-sm mb-4">
      <div className="dashboard-period-toolbar__header">
        <h6 className="dashboard-period-toolbar__title mb-0">
          <i className="bi bi-calendar-range me-2" />
          {t("app.analytics.periodFilter")}
        </h6>
      </div>

      <div className="dashboard-period-presets">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={`dashboard-period-preset-btn${activePresetId === preset.id ? " active" : ""}`}
            onClick={() => handlePreset(preset)}
          >
            {presetLabel(preset.id)}
          </button>
        ))}
      </div>

      <div className="dashboard-period-fields">
        <div className="dashboard-period-field">
          <Form.Label>{t("app.analytics.periodFrom")}</Form.Label>
          <DatePicker
            selected={periodFrom}
            onChange={(d) => onPeriodFromChange(d ? startOfDay(d) : null)}
            selectsStart
            startDate={periodFrom}
            endDate={periodTo}
            maxDate={periodTo || undefined}
            placeholderText={t("app.analytics.selectStartDate")}
            {...DATEPICKER_PROPS}
            className={`form-control${validationErrors.periodFrom ? " is-invalid" : ""}`}
          />
          {validationErrors.periodFrom && (
            <div className="invalid-feedback d-block">{errorMessage(validationErrors.periodFrom)}</div>
          )}
        </div>

        <div className="dashboard-period-field">
          <Form.Label>{t("app.analytics.periodTo")}</Form.Label>
          <DatePicker
            selected={periodTo}
            onChange={(d) => onPeriodToChange(d ? startOfDay(d) : null)}
            selectsEnd
            startDate={periodFrom}
            endDate={periodTo}
            minDate={periodFrom || undefined}
            placeholderText={t("app.analytics.selectEndDate")}
            {...DATEPICKER_PROPS}
            className={`form-control${validationErrors.periodTo || validationErrors.range ? " is-invalid" : ""}`}
          />
          {validationErrors.periodTo && (
            <div className="invalid-feedback d-block">{errorMessage(validationErrors.periodTo)}</div>
          )}
          {validationErrors.range && (
            <div className="invalid-feedback d-block">{errorMessage(validationErrors.range)}</div>
          )}
        </div>

        <div className="dashboard-period-actions">
          <button type="button" className="dashboard-period-apply-btn" onClick={handleApply}>
            <i className="bi bi-check2" aria-hidden />
            {t("app.analytics.applyPeriod")}
          </button>
        </div>
      </div>

      {resolvedLabel && (
        <div className="dashboard-period-resolved">
          <i className="bi bi-info-circle me-1" />
          {t("app.analytics.resolvedPeriod")}: <strong>{resolvedLabel}</strong>
        </div>
      )}
    </div>
  );
};

export default DashboardPeriodPicker;
